import { useCallback, useRef, useState } from 'react';
import Download from 'components/icons/download';
import HiddenAnchor from 'components/HiddenAnchor';
import { FileData } from './types';
import { StyledButtonIcon } from './styled';

interface Props {
    file?: FileData;
    onDownloadingChange?: (isDownloading: boolean) => void;
}

const DownloadButton = ({ file, onDownloadingChange }: Props) => {
    const { url = '', name, customMetadata } = file || {};
    const { originalName } = customMetadata || {};
    const hiddenLinkRef = useRef<HTMLAnchorElement>(null);
    const [isDownloading, setIsDownloading] = useState(false);

    const download = useCallback(async () => {
        if (!hiddenLinkRef.current || !url || isDownloading) return;

        setIsDownloading(true);
        onDownloadingChange?.(true);

        let downloadUrl = '';
        const originalExt = originalName?.split('.').pop();
        const baseName: string = name ?? 'download';
        const fileName: string = originalExt ? `${baseName}.${originalExt}` : baseName;

        try {
            const res = await fetch(url);
            const blob = await res.blob();
            downloadUrl = URL.createObjectURL(blob);

            const hiddenLink = hiddenLinkRef.current;
            if (!hiddenLink) return;

            hiddenLink.href = downloadUrl;
            hiddenLink.download = fileName;
            hiddenLink.click();
        } catch (error) {
            console.error('Error downloading file', error);
        } finally {
            if (downloadUrl) URL.revokeObjectURL(downloadUrl);
            setIsDownloading(false);
            onDownloadingChange?.(false);
        }
    }, [isDownloading, name, onDownloadingChange, originalName, url]);

    return (
        <>
            <HiddenAnchor ref={hiddenLinkRef} />
            <StyledButtonIcon
                icon={<Download />}
                tooltip="Download File"
                variant="border-filled"
                onClick={download}
                disabled={isDownloading}
                shaded
            />
        </>
    );
};

export default DownloadButton;
